import {
  SCORE_BAND,
  SCORE_BAND_COLOR,
  DEFAULT_SCORE_BANDS
} from '../../statusColors';

/**
 * The severity legend and its cut-offs, from the tenant's configuration
 * (FIN-13569).
 *
 * THE LEGEND WAS THREE HARDCODED STRINGS — "High (70+)", "Medium (40–69)",
 * "Low (<40)" — and it is the key an analyst reads every score on the timeline
 * against. Both halves were wrong for a configured tenant:
 *
 *   - THE NAMES. The codes are High / Good / Low. "Medium" is not one of them,
 *     as a code or as a display name, so the middle band was labelled with a
 *     word that appears nowhere in the tenant's settings. Where the names have
 *     been customised (High -> "Highfadsf") none of the three matched.
 *   - THE NUMBERS. 40 and 70 correspond to nothing. The configured bands on
 *     QA are 0–33 / 33–66 / 66–100, so a score of 68 read as "Medium" on the
 *     legend while the configuration scores it High.
 *
 * TWO SOURCES, because the configuration genuinely lives in two places:
 *   - `alert_level_details` via /common/alertLevelClassification — the CODES,
 *     their display names and their colours (`state.amlsCommon.alertLevelConfigs`)
 *   - the default scoring config — the CUT-OFFS `{ lowMax, mediumMax }`
 *     (`state.defaultScoringConfig`), the same pair the Settings → Scoring
 *     slider edits.
 *
 * THE SAME PATTERN THE SETTINGS SLIDER USES. `AlertLevelBandSlider` builds
 * `${lowName}: 0 – ${lowMax}` from exactly these two sources and renders
 * correctly; this is that, in the shape the timeline's legend needs.
 *
 * THE MIDDLE CODE IS `Good`, NOT `Medium` (FIN-12077) — the slider looks up
 * `getLevelLabel('Good')` for the same band. Our internal band key stays
 * `SCORE_BAND.MEDIUM` because it names a position, not a level.
 *
 * DEGRADES TO THE SHIPPED DEFAULTS, never to nothing. A deployment with no
 * configuration, a call that has not landed yet, or a shape we do not recognise
 * all yield the product's own default cut-offs and the capitalised codes — a
 * legend that is honest about the bands actually being applied, rather than an
 * empty strip or a blank key.
 */
const CODE_BY_BAND = {
  [SCORE_BAND.HIGH]: 'High',
  [SCORE_BAND.MEDIUM]: 'Good',
  [SCORE_BAND.LOW]: 'Low'
};

// Only ever called with a CODE_BY_BAND value, so no empty-input guard — one
// would be unreachable and would read as coverage rather than caution.
const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const finite = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * `{ lowMin, lowMax, mediumMax }` from the scoring config, or the shipped
 * defaults. `lowMin` is the sub-floor the tenant configures below which an
 * alert is suppressed; the LOW legend band begins at `lowMin`, not at 0, so
 * the timeline's key matches the one the Settings → Scoring slider renders.
 */
export const toScoreBands = (thresholds) => {
  const lowMax = finite(Number(thresholds?.lowMax));
  const mediumMax = finite(Number(thresholds?.mediumMax));

  // BOTH OR NEITHER, and only in a sane order. Half a configuration would
  // produce a legend whose bands overlap or invert, which is worse than the
  // default — and `lowMax >= mediumMax` is the same condition the settings
  // screen refuses to save.
  if (lowMax === undefined || mediumMax === undefined || lowMax >= mediumMax) {
    return DEFAULT_SCORE_BANDS;
  }

  // `lowMin` is optional — tenants who do not configure a floor send it as
  // absent, null, or an unparseable value. 0 matches the slider's own
  // `lowMinDisplay` fallback and keeps the LOW band spanning the full range
  // the tenant intended. An out-of-range or inverted lowMin (negative, or
  // >= lowMax) is treated as unset for the same reason as above — a
  // configuration the settings screen would not accept is not a legend.
  const parsedLowMin = finite(Number(thresholds?.lowMin));
  const lowMin =
    parsedLowMin === undefined || parsedLowMin < 0 || parsedLowMin >= lowMax
      ? 0
      : parsedLowMin;

  return { lowMin, lowMax, mediumMax };
};

const levelFor = (alertLevels, code) =>
  (Array.isArray(alertLevels) ? alertLevels : []).find(
    (level) =>
      typeof level?.code === 'string' &&
      level.code.toLowerCase() === code.toLowerCase()
  );

/**
 * The three legend entries, top band first, as the design orders them.
 *
 * The band STARTS mirror the Settings → Scoring slider, so an analyst reads
 * the same ranges on the configure screen and on the alert-detail timeline:
 *
 *   LOW    = lowMin        – lowMax
 *   GOOD   = lowMax    + 1 – mediumMax
 *   HIGH   = mediumMax + 1 – 100
 *
 * The previous shared-boundary form (`${lowMax}–${mediumMax}` for GOOD) put
 * the boundary score in two labels at once (89 in both LOW and GOOD on a
 * tenant with lowMax=89), and made HIGH read as `99–100` when the config
 * had HIGH as 100–100. `AlertLevelBandSlider` already derives the GOOD /
 * HIGH mins the same way (`goodMinDisplay = lowMax + 1`,
 * `highMinDisplay = mediumMax + 1`); the scoring classification in
 * `statusColors.js::scoreBand` is `score > mediumMax` → HIGH,
 * `score > lowMax` → GOOD, else LOW, so the labels and the classification
 * agree on integer scores (which is the shape the tenant configures).
 *
 * @param {Array} alertLevels  /common/alertLevelClassification rows
 * @param {{lowMin:number, lowMax:number, mediumMax:number}} bands
 */
export const toLegend = (alertLevels, bands) => {
  const { lowMin = 0, lowMax, mediumMax } = bands || DEFAULT_SCORE_BANDS;

  const range = {
    [SCORE_BAND.HIGH]: `${mediumMax + 1}–100`,
    [SCORE_BAND.MEDIUM]: `${lowMax + 1}–${mediumMax}`,
    [SCORE_BAND.LOW]: `${lowMin}–${lowMax}`
  };

  return [SCORE_BAND.HIGH, SCORE_BAND.MEDIUM, SCORE_BAND.LOW].map((key) => {
    const code = CODE_BY_BAND[key];
    const level = levelFor(alertLevels, code);
    // `displayName` is the CURRENT name; the code is the DEFAULT. An empty
    // string counts as unset — that is how an unconfigured row arrives.
    const name = level?.displayName || capitalise(code);

    return {
      key,
      label: `${name} (${range[key]})`,
      color: level?.color || SCORE_BAND_COLOR[key]
    };
  });
};

export default toLegend;
